import useHasPermissionUser from "@/composables/useHasPermissionUser";
import { verifyIsDuplicateData } from "@/helpers/verifyIsDuplicateData";
import { HotelGroups } from "@/internal/modules/providers/interfaces/HotelGroup";
import { useQueryClient } from "@tanstack/vue-query";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { computed, reactive, ref } from "vue";
import { useToast } from "vue-toastification";
import useHotelGroups from "./useHotelGroup";
import useSearchHotelGroups from "./useSearchHotelGroups";

const useHotelGroupForm = (group?: HotelGroups) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const modalAction = ref(false);

  const actionModal = () => {
    modalAction.value = !modalAction.value;
  };

  const modalActionEdit = ref(false);

  const actionEdit = () => {
    modalActionEdit.value = !modalActionEdit.value;
  };

  const { data, setQuery, refetch, isLoading } = useSearchHotelGroups({
    immediate: false,
    queryKey: "search-HotelGroups-coincidences",
  });

  const isCoincidence = ref(false);
  const isConfirmed = ref(false);

  const form = reactive({
    id: group?.id || 0,
    name: group?.name || "",
  });

  const duplicateHotels = computed(() => {
    return data.value?.data?.filter(
      (data) => data.name.toLocaleLowerCase() === form.name.toLocaleLowerCase(),
    );
  });

  const formRules = {
    name: {
      required: helpers.withMessage("El nombre es requerido", required),
    },
  };

  const {
    createMutationAsync,
    createMutationLoading,
    updateMutationAsync,
    updateMutationLoading,
  } = useHotelGroups({
    immediate: false,
  });

  const formVuelidate = useVuelidate(formRules, form);

  const handleEvaluation = async () => {
    await verifyIsDuplicateData({
      data: data.value?.data!,
      form,
      setQuery,
      refetch: async () => {
        await refetch();
      },
      formVuelidate,
      duplicateItems: duplicateHotels,
      toast,
      handleCreate,
      isConfirmed,
      isCoincidence,
    });
  };

  const handleCreate = async () => {
    isConfirmed.value = true;

    const data = await createMutationAsync(form.name.toLocaleLowerCase());

    actionModal();
    clearState();

    await queryClient.invalidateQueries(["hotelGroups", "list-hotel-groups"]);
    await queryClient.invalidateQueries([
      "hotelGroups",
      "search-HotelGroups-coincidences",
    ]);
  };

  const { hasPermissionUser } = useHasPermissionUser();

  const handleEvaluationEdit = async () => {
    if (!hasPermissionUser("update-internal", "App\\Models\\Group")) return;

    await verifyIsDuplicateData({
      data: data.value?.data!,
      form,
      setQuery,
      refetch: async () => {
        await refetch();
      },
      formVuelidate,
      duplicateItems: duplicateHotels,
      toast,
      handleCreate: handleEdit,
      isConfirmed,
      isCoincidence,
    });
  };

  const handleEdit = async () => {
    isConfirmed.value = true;

    const data = await updateMutationAsync({
      id: form.id,
      name: form.name.toLowerCase(),
    });

    actionEdit();

    isCoincidence.value = false;
    isConfirmed.value = false;

    await queryClient.invalidateQueries(["hotelGroup"]);
    await queryClient.invalidateQueries(["hotelGroups", "list-hotel-groups"]);
    await queryClient.invalidateQueries([
      "hotelGroups",
      "search-HotelGroups-coincidences",
    ]);
  };

  const clearState = async () => {
    form.name = "";
    isCoincidence.value = false;
    isConfirmed.value = false;
    form.id = 0;
  };

  return {
    form,
    duplicateHotels,
    hotels: data,
    isCoincidence,
    isConfirmed,
    modalAction,
    handleEvaluation,
    handleCreate,
    clearState,
    actionModal,
    isLoading,
    createMutationLoading,

    handleEvaluationEdit,
    handleEdit,
    actionEdit,
    modalActionEdit,
    updateMutationLoading,
  };
};

export default useHotelGroupForm;
